import {
  type DefaultError,
  type EnsureQueryDataOptions,
  isCancelledError,
  QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { isApiError } from '../lib/api'

/** Client errors (4xx) are answers, not glitches: never retried. Network/5xx get two more tries. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false
  return failureCount < 2
}

/**
 * Route loaders wait on `ensureQueryData`, which is not an observer of the query. When the only
 * component observing that query (e.g. a breadcrumb) remounts while the request is in flight,
 * TanStack Query cancels it because nobody observes it any more, and the loader would reject with
 * a CancelledError: the page showed "Something went wrong" on slow connections. A cancelled load
 * is not a failure, so the loader simply asks again (bounded, in case something keeps cancelling).
 */
class LoaderSafeQueryClient extends QueryClient {
  override async ensureQueryData<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
  >(options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>): Promise<TData> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await super.ensureQueryData(options)
      } catch (error) {
        if (!isCancelledError(error) || attempt >= 3) throw error
      }
    }
  }
}

export function createQueryClient(): QueryClient {
  return new LoaderSafeQueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  })
}

export const queryClient = createQueryClient()
